import { z } from 'zod';
// User Validators
export const phoneNumberSchema = z
    .string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(15, 'Phone number must not exceed 15 digits')
    .regex(/^[6-9]\d{9}$/, 'Invalid Indian phone number');
export const emailSchema = z.string().email('Invalid email address');
export const addressSchema = z.object({
    id: z.string(),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    phone: phoneNumberSchema,
    addressLine1: z.string().min(5, 'Address is too short'),
    addressLine2: z.string().optional(),
    city: z.string().min(2, 'City is required'),
    state: z.string().min(2, 'State is required'),
    pincode: z.string().regex(/^[1-9][0-9]{5}$/, 'Invalid pincode'),
    isDefault: z.boolean(),
});
// Book Validators
export const bookSchema = z.object({
    title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
    author: z.string().min(1, 'Author is required').max(100, 'Author name is too long'),
    isbn: z.string().optional(),
    description: z.string().min(10, 'Description must be at least 10 characters'),
    price: z.number().positive('Price must be positive'),
    discountedPrice: z.number().positive().optional(),
    stock: z.number().int().nonnegative('Stock cannot be negative'),
    categoryId: z.string().min(1, 'Category is required'),
    language: z.string().min(1, 'Language is required'),
    tags: z.array(z.string()),
    isFeatured: z.boolean(),
    isActive: z.boolean(),
});
// Order Validators
export const orderItemSchema = z.object({
    productId: z.string(),
    title: z.string(),
    quantity: z.number().int().positive().max(10, 'Maximum 10 items allowed per book'),
    price: z.number().positive(),
    discountedPrice: z.number().positive().optional(),
    imageUrl: z.string().url(),
});
export const orderSchema = z.object({
    userId: z.string(),
    items: z.array(orderItemSchema).min(1, 'Order must have at least one item'),
    shippingAddress: addressSchema,
    couponCode: z.string().optional(),
    paymentMethod: z.enum(['razorpay', 'cod']),
    customerNotes: z.string().max(500).optional(),
});
// Coupon Validators
export const coupon = z
    .object({
    code: z
        .string()
        .min(3, 'Coupon code must be at least 3 characters')
        .max(20, 'Coupon code must not exceed 20 characters')
        .regex(/^[A-Z0-9]+$/, 'Coupon code must contain only uppercase letters and numbers'),
    discountType: z.enum(['percentage', 'fixed_amount']),
    discountValue: z.number().positive('Discount value must be positive'),
    minOrderValue: z.number().nonnegative('Minimum order value cannot be negative'),
    maxDiscount: z.number().positive().optional(),
    usageLimit: z.number().int().positive('Usage limit must be positive'),
    isActive: z.boolean(),
    validFrom: z.date(),
    validTo: z.date(),
})
    .superRefine((data, ctx) => {
    if (data.validTo <= data.validFrom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validTo'],
            message: 'Valid To date must be after Valid From date',
        });
    }
});
// Category Validators
export const categorySchema = z.object({
    name: z.string().min(2, 'Category name must be at least 2 characters'),
    description: z.string().optional(),
    parentId: z.string().nullable().optional(),
    order: z.number().int().nonnegative(),
    isActive: z.boolean(),
});
// Testimonial Validators
export const testimonialSchema = z.object({
    userName: z.string().min(2, 'Name must be at least 2 characters'),
    rating: z.number().int().min(1, 'Rating must be at least 1').max(5, 'Rating cannot exceed 5'),
    comment: z
        .string()
        .min(10, 'Comment must be at least 10 characters')
        .max(500, 'Comment is too long'),
});
// Login Validators
export const loginSchema = z.object({
    phoneNumber: phoneNumberSchema,
});
export const otpSchema = z.object({
    otp: z.string().length(6, 'OTP must be 6 digits').regex(/^\d+$/, 'OTP must contain only numbers'),
});
// Validation helper functions
export const validatePhoneNumber = (phone) => {
    try {
        phoneNumberSchema.parse(phone);
        return true;
    }
    catch {
        return false;
    }
};
export const validateEmail = (email) => {
    try {
        emailSchema.parse(email);
        return true;
    }
    catch {
        return false;
    }
};
export const validatePincode = (pincode) => {
    return /^[1-9][0-9]{5}$/.test(pincode);
};
export const safeParse = (schema, data) => {
    const result = schema.safeParse(data);
    if (!result.success) {
        throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
};
